// imports
import {Dispatch, SetStateAction, useState, useMemo, useEffect} from 'react';

// local imports
import ReusableMenu from '../ReusableMenu';
import { useGetAwdConfigQuery, selectModelsBySector } from '../../../../services/awdConfigApi';
import { initialWaterDataState } from '../../../../store/waterDataSlice';
import { getUrlParam } from '../../../../utils/getUrlParam';

// Define the props interface
interface ModelMenuProps {
  selectedSector: string;
  setSelectedModel: Dispatch<SetStateAction<string | null>>;
}

export default function ModelMenu({
  selectedSector,
  setSelectedModel,
}: ModelMenuProps) {
  const { data, isError, isFetching } = useGetAwdConfigQuery();

  // For tracking which model is currently selected
  const [selectedIndex, setSelectedIndex] = useState(2);
  const [hasInitialized, setHasInitialized] = useState(false);

  const modelOptions = useMemo(() => {
    // Populate menu with error notice when there's an API error
    const defaultErrorOption = {
      key: 'error',
      value: 'No models available - select a different sector',
    };

    // Populate menu with no data notice when API call is successful, but no data are returned
    const noModelsOption = {
      key: 'no-models',
      value: 'No models available - select a different sector',
    };

    if (isError) {
      // If there's an error, set the model options to include the error message
      return [defaultErrorOption];
    } else if (!data || data.models.length === 0) {
      // If there are no data, set the model options to include the no data message
      return [noModelsOption];
    } else {
      // If data are returned, map the models to options
      const models = selectModelsBySector(data.models, selectedSector);
      return models.length === 0 ? [noModelsOption] : models;
    }
  }, [isError, data, selectedSector]);

  // Effect to set selectedModel when modelOptions are updated
  useEffect(() => {
    // Don't initialize until we have a selectedSector and data has loaded
    if (!selectedSector || isFetching || hasInitialized) {
      return;
    }

    if (modelOptions.length > 0) {
      const key = modelOptions[0].key;
      if (key === 'error' || key === 'no-models') {
        // Handle error states
        setSelectedModel(null);
        // Resetting selectedIndex to 0 as part of a guarded one-time initialization
        // block (hasInitialized prevents repeated execution). This keeps the UI
        // consistent when the model list contains only an error placeholder.
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setSelectedIndex(0);
        setHasInitialized(true);
      } else {
        // Try to find model from URL first
        const modelFromUrl = getUrlParam('model');

        if (
          modelFromUrl &&
          modelOptions.some((opt) => opt.key === modelFromUrl)
        ) {
          // URL param takes precedence - use URL value if it exists and is valid
          const foundIndex = modelOptions.findIndex(
            (opt) => opt.key === modelFromUrl
          );
          setSelectedIndex(foundIndex);
          setSelectedModel(modelFromUrl);
        } else {
          // No valid URL param - try to use Redux initial state, fallback to first model
          const defaultModel = initialWaterDataState.selectedModel;
          const defaultIndex = modelOptions.findIndex(
            (opt) => opt.key === defaultModel
          );

          if (defaultIndex !== -1) {
            // Redux default model exists in options
            setSelectedModel(defaultModel);
            setSelectedIndex(defaultIndex);
          } else {
            // Redux default model not in options, use first available model
            const firstModel = modelOptions[0].key;
            setSelectedModel(firstModel);
            setSelectedIndex(0);
          }
        }
        setHasInitialized(true);
      }
    }
  }, [
    modelOptions,
    selectedSector,
    isFetching,
    hasInitialized,
    setSelectedModel,
  ]);

  // Reset initialization flag when selectedSector changes
  useEffect(() => {
    // Resetting a boolean flag so the initialization effect re-runs after the
    // sector changes and new model data loads. This is a single-state update
    // that triggers one controlled re-initialization cycle.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setHasInitialized(false);
  }, [selectedSector]);

  // Effect to handle loading and error states
  useEffect(() => {
    if (isError || modelOptions.length === 0) {
      // Resetting selectedIndex to 0 when data is unavailable or an error occurs
      // so the UI doesn't reference a stale index into an empty/error list.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedIndex(0);
    }
  }, [isError, modelOptions]);

  // Show this message if the model options/menu take a long time to load
  if (isFetching) return <div>Loading Model Menu...</div>;

  // Update the 'learn more' link to reflect the selected model
  const cloudfrontPrefix = import.meta.env.DEV ? `${import.meta.env.VITE_NWDC_S3_URL}` : import.meta.env.VITE_CLOUDFRONT_PROXY_PREFIX;
  const learnMoreLink = `${cloudfrontPrefix}/data-catalog/sector/${selectedSector}/model/${modelOptions[selectedIndex]?.key}`;

  // Update the selected index whenever a new model is selected
  const handleSelect = (index: number) => {
    if (!isError && modelOptions[index].key !== 'no-models') {
      setSelectedIndex(index);
      // Get the key of the selected model
      const selectedModel = modelOptions[index].key;

      // Update local state only - URL params updated when Update Map is clicked
      setSelectedModel(selectedModel);
    }
  };

  return (
    <div>
      <ReusableMenu
        title="Model"
        options={modelOptions}
        selectedIndex={selectedIndex}
        onSelect={handleSelect}
        learnMoreLink={learnMoreLink}
        disabled={!!isError || modelOptions[0]?.key === 'no-models'}
        menuItemTestId={(key) => `menu-item-${key}`}
      />
    </div>
  );
}
